/**
 * Utility function to clear all authentication cookies and storage
 * Can be called from browser console: window.clearAllAuthCookies()
 */
export const clearAllAuthCookies = () => {
  // Clear localStorage
  const localStorageKeys = ['jwt_token', 'redirectAfterLogin', 'sso_return_url'];
  localStorageKeys.forEach(key => {
    localStorage.removeItem(key);
  });
  
  // Clear all MSAL tokens
  Object.keys(localStorage).forEach(key => {
    if (key.startsWith('msal')) {
      localStorage.removeItem(key);
    }
  });
  
  // Clear sessionStorage
  sessionStorage.clear();
  
  // Clear non-httpOnly cookies only
  // Note: auth_token is httpOnly and cannot be cleared by frontend
  const cookieNames = [
    'jwt', 'session_token', 'access_token', 'refresh_token',
    'msal.cache.encryption', 'msal.idtoken', 'msal.accesstoken', 'msal.refreshtoken'
  ];
  
  // Only clear cookies for current domain and its valid parent domains
  const currentHostname = window.location.hostname;
  const domains = [''];
  
  // Add current domain variations only if they make sense
  if (currentHostname === 'localhost') {
    domains.push('localhost', '.localhost');
  } else if (currentHostname.includes('talavera.ai')) {
    domains.push('.talavera.ai', currentHostname);
  } else if (currentHostname.includes('web.app')) {
    // Firebase hosting domains
    domains.push(currentHostname);
  }
  
  const paths = ['/', '/api', '/auth'];
  
  let cookiesCleared = 0;
  
  cookieNames.forEach(cookieName => {
    domains.forEach(domain => {
      paths.forEach(path => {
        // Clear with domain and secure flags
        if (domain) {
          document.cookie = `${cookieName}=; path=${path}; expires=Thu, 01 Jan 1970 00:00:01 GMT; domain=${domain}; secure; samesite=none`;
          document.cookie = `${cookieName}=; path=${path}; expires=Thu, 01 Jan 1970 00:00:01 GMT; domain=${domain}; samesite=lax`;
          document.cookie = `${cookieName}=; path=${path}; expires=Thu, 01 Jan 1970 00:00:01 GMT; domain=${domain}`;
        }
        // Clear without domain (current domain only)
        document.cookie = `${cookieName}=; path=${path}; expires=Thu, 01 Jan 1970 00:00:01 GMT; secure; samesite=none`;
        document.cookie = `${cookieName}=; path=${path}; expires=Thu, 01 Jan 1970 00:00:01 GMT; samesite=lax`;
        document.cookie = `${cookieName}=; path=${path}; expires=Thu, 01 Jan 1970 00:00:01 GMT`;
        cookiesCleared += 6;
      });
    });
  });
  
  return {
    localStorageCleared: localStorageKeys.length,
    sessionStorageCleared: true,
    cookieVariationsCleared: cookiesCleared,
    remainingCookies: document.cookie
  };
};

// Make it available globally for console access
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).clearAllAuthCookies = clearAllAuthCookies;
}

// Specific function to clear MSAL cookies and cache
export const clearMSALCookies = () => {
  // Clear all MSAL localStorage entries
  Object.keys(localStorage).forEach(key => {
    if (key.startsWith('msal')) {
      localStorage.removeItem(key);
    }
  });
  
  // Clear MSAL cookies specifically
  const msalCookieNames = ['msal.cache.encryption', 'msal.idtoken', 'msal.accesstoken', 'msal.refreshtoken'];
  
  // Only clear cookies for current domain and its valid parent domains
  const currentHostname = window.location.hostname;
  const domains = [''];
  
  // Add current domain variations only if they make sense
  if (currentHostname === 'localhost') {
    domains.push('localhost', '.localhost');
  } else if (currentHostname.includes('talavera.ai')) {
    domains.push('.talavera.ai', currentHostname);
  } else if (currentHostname.includes('web.app')) {
    // Firebase hosting domains
    domains.push(currentHostname);
  }
  
  const paths = ['/', '/api', '/auth'];
  
  msalCookieNames.forEach(cookieName => {
    domains.forEach(domain => {
      paths.forEach(path => {
        if (domain) {
          document.cookie = `${cookieName}=; path=${path}; expires=Thu, 01 Jan 1970 00:00:01 GMT; domain=${domain}; secure; samesite=none`;
          document.cookie = `${cookieName}=; path=${path}; expires=Thu, 01 Jan 1970 00:00:01 GMT; domain=${domain}; samesite=lax`;
          document.cookie = `${cookieName}=; path=${path}; expires=Thu, 01 Jan 1970 00:00:01 GMT; domain=${domain}`;
        }
        document.cookie = `${cookieName}=; path=${path}; expires=Thu, 01 Jan 1970 00:00:01 GMT; secure; samesite=none`;
        document.cookie = `${cookieName}=; path=${path}; expires=Thu, 01 Jan 1970 00:00:01 GMT; samesite=lax`;
        document.cookie = `${cookieName}=; path=${path}; expires=Thu, 01 Jan 1970 00:00:01 GMT`;
      });
    });
  });
};

// Also export a simpler version for immediate use
export const quickClearCookies = () => {
  // Get all cookies
  const cookies = document.cookie.split(';');
  
  // Clear each cookie
  cookies.forEach(cookie => {
    const eqPos = cookie.indexOf('=');
    const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
    
    if (name) {
      // Clear with multiple domain/path combinations
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/`;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/; domain=${window.location.hostname}`;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/; domain=.${window.location.hostname}`;
    }
  });
};

if (typeof window !== 'undefined') {
  const globalWindow = window as unknown as Record<string, unknown>;
  globalWindow.quickClearCookies = quickClearCookies;
  globalWindow.clearMSALCookies = clearMSALCookies;
}
